import React, { useState, useMemo } from 'react';
import { Modal } from '../../ui/Modal';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Badge';
import { Spinner } from '../../ui/Spinner';
import { useNodeRoster } from '../../../hooks/useNodeRoster';
import { DirectoryMember } from '../../../types';
import { Search, Users, CheckSquare, Square, Check } from 'lucide-react';
import { cn } from '../../../lib/utils';

export interface NodeRosterPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (selectedMembers: DirectoryMember[]) => void;
  title?: string;
  description?: string;
  confirmLabel?: string;
  allowEmpty?: boolean;
}

export const NodeRosterPickerModal: React.FC<NodeRosterPickerModalProps> = ({
  isOpen,
  onClose,
  onImport,
  title = 'Import Organization Roster',
  description,
  confirmLabel = 'Import Selected',
  allowEmpty = false
}) => {
  const { members, isLoading, nodeName } = useNodeRoster();
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const filteredMembers = useMemo(() => {
    if (!search.trim()) return members;
    const q = search.toLowerCase();
    return members.filter(m => 
      m.name?.toLowerCase().includes(q) ||
      m.email?.toLowerCase().includes(q) ||
      m.role?.toLowerCase().includes(q)
    );
  }, [members, search]);

  const toggleMember = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredMembers.length && filteredMembers.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredMembers.map(m => m.id)));
    }
  };

  const handleConfirm = () => {
    const selected = members.filter(m => selectedIds.has(m.id));
    onImport(selected);
    onClose();
  };

  const isAllSelected = filteredMembers.length > 0 && selectedIds.size === filteredMembers.length;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      className="max-w-2xl rounded-none border-4 border-black dark:border-white shadow-[8px_8px_0px_#000000] dark:shadow-[8px_8px_0px_#FFFFFF] p-0"
    >
      <div className="p-6 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b-2 border-black dark:border-white pb-3">
          <div>
            <div className="inline-flex items-center gap-1.5 text-[10px] font-mono font-black uppercase tracking-widest bg-[#FFD600] text-black px-2 py-0.5 border border-black mb-1">
              <Users size={12} />
              <span>{nodeName || 'Organization'} Directory</span>
            </div>
            <p className="text-xs font-bold text-gray-600 dark:text-gray-300">
              {description || `Select members to add to the tool roster.`}
            </p>
          </div>
          <Badge className="rounded-none border-2 border-black bg-black text-white dark:bg-white dark:text-black font-mono text-xs px-3 py-1">
            {selectedIds.size} of {members.length} Selected
          </Badge>
        </div>

        {/* Search & Bulk Toggle */}
        <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center justify-between">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Filter by name, email, or role..."
              className="w-full pl-9 pr-3 py-2 text-xs font-bold border-2 border-black dark:border-white bg-white dark:bg-black text-black dark:text-white rounded-none focus:outline-none focus:ring-2 focus:ring-[#0044FF]"
            />
          </div>

          <Button
            size="sm"
            variant="secondary"
            onClick={toggleSelectAll}
            className="rounded-none border-2 border-black dark:border-white font-mono text-xs uppercase tracking-wider shrink-0"
          >
            {isAllSelected ? (
              <span className="flex items-center gap-1.5"><Square size={14} /> Deselect All</span>
            ) : (
              <span className="flex items-center gap-1.5"><CheckSquare size={14} /> Select All ({filteredMembers.length})</span>
            )}
          </Button>
        </div>

        {/* Member Checklist */}
        <div className="border-2 border-black dark:border-white max-h-72 overflow-y-auto divide-y-2 divide-black/10 dark:divide-white/10 bg-[#F5F5F0] dark:bg-slate-900">
          {isLoading ? (
            <div className="py-12 flex flex-col items-center justify-center text-center space-y-2">
              <Spinner size="md" />
              <span className="text-xs font-mono font-bold uppercase tracking-widest text-gray-500">Loading directory...</span>
            </div>
          ) : filteredMembers.length === 0 ? (
            <div className="py-12 text-center text-gray-500 font-mono text-xs uppercase">
              {search ? 'No members match search query.' : 'No members found in organization roster.'}
            </div>
          ) : (
            filteredMembers.map(member => {
              const isSelected = selectedIds.has(member.id);
              return (
                <div
                  key={member.id}
                  onClick={() => toggleMember(member.id)}
                  className={cn(
                    "flex items-center justify-between p-3 cursor-pointer transition-colors select-none",
                    isSelected ? "bg-[#FFD600]/20 hover:bg-[#FFD600]/30" : "hover:bg-black/5 dark:hover:bg-white/5"
                  )}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={cn(
                      "w-5 h-5 border-2 border-black dark:border-white flex items-center justify-center transition-all",
                      isSelected ? "bg-black text-white dark:bg-white dark:text-black" : "bg-white dark:bg-black"
                    )}>
                      {isSelected && <Check size={14} className="stroke-[3]" />}
                    </div>

                    <div className="w-8 h-8 rounded-none border-2 border-black dark:border-white bg-[#0044FF] text-white flex items-center justify-center font-bold text-xs shrink-0">
                      {member.name ? member.name.charAt(0).toUpperCase() : 'M'}
                    </div>

                    <div className="min-w-0">
                      <p className="text-xs font-black uppercase tracking-tight truncate text-black dark:text-white">
                        {member.name}
                      </p>
                      <p className="text-[10px] font-mono text-gray-500 truncate">
                        {member.email || 'No email'}
                      </p>
                    </div>
                  </div>

                  <div className="shrink-0 ml-3">
                    <span className="text-[9px] font-mono font-bold uppercase tracking-widest border border-black dark:border-white px-2 py-0.5 bg-white dark:bg-black text-black dark:text-white">
                      {member.role || 'Member'}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex justify-between items-center pt-3 border-t-2 border-black dark:border-white">
          <span className="text-[10px] font-mono uppercase font-bold text-gray-500">
            {selectedIds.size} ready to import
          </span>

          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={onClose}
              className="rounded-none border-2 border-black dark:border-white px-4 text-xs font-bold uppercase font-mono"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirm}
              disabled={!allowEmpty && selectedIds.size === 0}
              className="rounded-none shadow-[3px_3px_0px_#000000] dark:shadow-[3px_3px_0px_#FFFFFF] bg-black text-white dark:bg-white dark:text-black font-bold uppercase text-xs px-6 hover:bg-[#0044FF] hover:text-white"
            >
              {confirmLabel} ({selectedIds.size})
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
